import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UserProfile } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { Ban, CheckCircle, AlertTriangle } from 'lucide-react';

interface UserBlockDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserProfile;
}

export function UserBlockDialog({
  isOpen,
  onClose,
  onSuccess,
  user
}: UserBlockDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const isBlocked = (user as any).is_blocked;

  const handleToggleBlock = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_blocked: !isBlocked })
        .eq('id', user.id);

      if (error) throw error;

      const action = isBlocked ? 'разблокирован' : 'заблокирован';
      toast.success(`Пользователь ${user.name} был ${action}`);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Ошибка: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isBlocked ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                Разблокировать пользователя
              </>
            ) : (
              <>
                <Ban className="h-5 w-5 text-red-600" />
                Заблокировать пользователя
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium">{user.name}</p>
              <p className="text-sm text-muted-foreground">
                {user.email || user.phone}
              </p>
            </div>
          </div>

          {isBlocked ? (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-green-800">Разблокировка пользователя</p>
                <p className="text-green-700 mt-1">
                  Пользователь сможет войти в систему и использовать все доступные функции.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">Блокировка пользователя</p>
                <p className="text-red-700 mt-1">
                  Пользователь не сможет войти в систему. Все активные сессии будут завершены.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Отмена
          </Button>
          <Button
            variant={isBlocked ? "default" : "destructive"}
            onClick={handleToggleBlock}
            disabled={isLoading}
          >
            {isLoading ? (
              <>Обработка...</>
            ) : isBlocked ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                Разблокировать
              </>
            ) : (
              <>
                <Ban className="h-4 w-4 mr-1" />
                Заблокировать
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}